import React, { createContext, useCallback, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { authService, AuthResult, UserProfile } from '../services/authService';
import { subscriptionService, Subscription } from '../services/subscriptionService';
import { ResourceStatus } from '../models/account';

interface AuthContextType {
    user: User | null; profile: UserProfile | null; session: Session | null; loading: boolean;
    profileStatus: ResourceStatus; subscriptionStatus: ResourceStatus;
    subscription: Subscription | null; isPaidUser: boolean;
    signIn: (email: string, password: string) => Promise<AuthResult>;
    signUp: (email: string, password: string, fullName?: string, phone?: string, company?: string, role?: string, bio?: string) => Promise<AuthResult>;
    signOut: () => Promise<void>;
    signInWithGoogle: () => Promise<void>; signInWithGithub: () => Promise<void>;
    refreshProfile: () => Promise<UserProfile | null>;
    refreshSubscription: () => Promise<Subscription | null>;
    publishProfile: (profile: UserProfile) => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Compatibility facade: each resource owns its request ordering and freshness.
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [subscription, setSubscription] = useState<Subscription | null>(null);
    const [profileStatus, setProfileStatus] = useState<ResourceStatus>('loading');
    const [subscriptionStatus, setSubscriptionStatus] = useState<ResourceStatus>('loading');
    const mounted = useRef(false), activeId = useRef<string | null>(null), generation = useRef(0);
    const profileRequest = useRef(0), planRequest = useRef(0);
    const profileFetchedAt = useRef(0), planFetchedAt = useRef(0);

    const loadProfile = useCallback(async (id: string): Promise<UserProfile | null> => {
        const authVersion = generation.current, request = ++profileRequest.current;
        const current = () => mounted.current && activeId.current === id && generation.current === authVersion && profileRequest.current === request;
        try {
            const result = await authService.getProfile(id);
            if (current()) { setProfile(result); setProfileStatus(result ? 'ready' : 'missing'); profileFetchedAt.current = Date.now(); }
            return current() ? result : null;
        } catch {
            if (current()) setProfileStatus('error');
            throw new Error('Profile details could not be loaded. Please retry.');
        }
    }, []);
    const loadPlan = useCallback(async (id: string): Promise<Subscription | null> => {
        const authVersion = generation.current, request = ++planRequest.current;
        const current = () => mounted.current && activeId.current === id && generation.current === authVersion && planRequest.current === request;
        try {
            const result = await subscriptionService.getSubscription(id);
            if (current()) { setSubscription(result); setSubscriptionStatus(result ? 'ready' : 'missing'); planFetchedAt.current = Date.now(); }
            return current() ? result : null;
        } catch {
            if (current()) setSubscriptionStatus('error');
            throw new Error('Your plan could not be loaded. Please retry.');
        }
    }, []);
    const applyAuthState = useCallback((next: Session | null, explicitUser?: User | null) => {
        const nextUser = explicitUser === undefined ? next?.user ?? null : explicitUser;
        const id = nextUser?.id ?? null;
        const changed = activeId.current !== id;
        setSession(next); setUser(nextUser);
        if (changed) {
            ++generation.current; activeId.current = id;
            ++profileRequest.current; ++planRequest.current;
            setProfile(null); setSubscription(null); subscriptionService.clearCache();
            profileFetchedAt.current = 0; planFetchedAt.current = 0;
            setProfileStatus(id ? 'loading' : 'missing'); setSubscriptionStatus(id ? 'loading' : 'missing');
            if (id) { void loadProfile(id).catch(() => {}); void loadPlan(id).catch(() => {}); }
        }
    }, [loadPlan, loadProfile]);
    const refreshProfile = useCallback(async () => activeId.current ? loadProfile(activeId.current) : null, [loadProfile]);
    const refreshSubscription = useCallback(async () => {
        subscriptionService.clearCache();
        return activeId.current ? loadPlan(activeId.current) : null;
    }, [loadPlan]);
    const publishProfile = useCallback((next: UserProfile) => {
        if (!mounted.current || next.id !== activeId.current) return;
        ++profileRequest.current; profileFetchedAt.current = Date.now();
        setProfile(current => current && (current.revision ?? -1) > (next.revision ?? -1) ? current : next);
        setProfileStatus('ready');
    }, []);

    useEffect(() => {
        mounted.current = true;
        const authGeneration = generation;
        const initialGeneration = generation.current;
        void authService.getSession().then(next => {
            if (mounted.current && generation.current === initialGeneration) { applyAuthState(next); setLoading(false); }
        }).catch(() => {
            if (mounted.current && generation.current === initialGeneration) { applyAuthState(null); setLoading(false); }
        });
        const { data: { subscription: listener } } = authService.onAuthStateChange((_event, next) => {
            if (mounted.current) { applyAuthState(next); setLoading(false); }
        });
        return () => { mounted.current = false; activeId.current = null; ++authGeneration.current; listener.unsubscribe(); };
    }, [applyAuthState]);
    useEffect(() => {
        const refresh = () => {
            const id = activeId.current;
            if (!id || document.visibilityState === 'hidden') return;
            if (Date.now() - profileFetchedAt.current > 300_000) void loadProfile(id).catch(() => {});
            if (Date.now() - planFetchedAt.current > 60_000) void loadPlan(id).catch(() => {});
        };
        const timer = window.setInterval(refresh, 60_000);
        window.addEventListener('focus', refresh);
        return () => { clearInterval(timer); window.removeEventListener('focus', refresh); };
    }, [loadPlan, loadProfile]);

    const signIn = async (email: string, password: string) => {
        const result = await authService.signIn(email, password);
        if (result.success && result.user) { applyAuthState(result.session ?? null, result.user); setLoading(false); }
        return result;
    };
    const signUp = async (email: string, password: string, fullName?: string, phone?: string, company?: string, role?: string, bio?: string) => {
        const result = await authService.signUp(email, password, fullName, phone, company, role, bio);
        if (result.success && result.session) { applyAuthState(result.session); setLoading(false); }
        return result;
    };
    const signOut = async () => { await authService.signOut(); applyAuthState(null); };
    const isPaidUser = Boolean(subscription && subscription.plan !== 'free' && subscription.status === 'active' &&
        (!subscription.expires_at || new Date(subscription.expires_at) > new Date()));
    return <AuthContext.Provider value={{ user, profile, session, loading, subscription, isPaidUser, profileStatus, subscriptionStatus,
        signIn, signUp, signOut, signInWithGoogle: authService.signInWithGoogle, signInWithGithub: authService.signInWithGithub,
        refreshProfile, refreshSubscription, publishProfile }}>{children}</AuthContext.Provider>;
};
export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within an AuthProvider');
    return context;
};
